import { requiredString } from "components/form/utils";

import z from "zod";

const capitalize = (str) => {
  if (!str) return "";
  return str
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
};

// System fields to exclude from forms
const SYSTEM_FIELDS = [
  "id",
  "date_created",
  "date_updated",
  "user_created",
  "user_updated",
  "sort",
];

// Map Directus field types to form field types
const FIELD_TYPE_MAPPING = {
  // Text fields
  string: "text",
  text: "textarea",
  uuid: "text",
  varchar: "text",
  hash: "password",

  // Number fields
  integer: "number",
  bigInteger: "number",
  float: "number",
  decimal: "number",

  // Date/Time fields
  date: "date",
  time: "time",
  dateTime: "datetime-local",
  timestamp: "datetime-local",

  // Boolean fields
  boolean: "checkbox",

  // Selection fields
  json: "json",

  // File fields
  csv: "file",

  // Alias fields (dynamic/unknown structure)
  alias: "json",

  // Relationship fields
  m2o: "select", // Many-to-One
  o2m: "multiselect", // One-to-Many
  m2m: "multiselect", // Many-to-Many
  m2a: "select", // Many-to-Any
};

// Map Directus fields to form-compatible fields
export function mapDirectusFieldsToFormFields(directusFields) {
  return directusFields
    .filter((field) => !SYSTEM_FIELDS.includes(field.field))
    .filter((field) => {
      return !field.meta?.hidden && field.schema;
    })
    .map((field) => {
      const customField = {
        name: field.field,
        label: capitalize(field.meta?.display_name || field.field),
        type: FIELD_TYPE_MAPPING[field.type] || "text",

        required: field.meta.required || false,

        note: field.meta.note || "",
        options: field.meta?.options || {},
        validation: field.meta?.validation || {},
        validationMessage: field.meta?.validation_message || "",
        readonly: field.meta?.readonly || false,
        description: field.meta?.note || "",
        directusType: field.type,
        directusField: field,
        width: field.meta?.width,

        // Relationship specific data
        ...(field.meta?.special?.includes("m2o") && {
          relatedCollection: field.schema?.foreign_key_table,
          displayTemplate: field.meta?.options?.template || "{{name}}",
          valueField: field.schema?.foreign_key_column || "id",
          isRelationship: true,
        }),

        // Selection options
        ...((field.meta?.interface === "select-dropdown" ||
          field.meta?.interface === "select-radio") && {
          choices: field.meta?.options?.choices || [],
        }),
      };

      return customField;
    })
    .sort(
      (a, b) =>
        (a.directusField.meta?.sort || 0) - (b.directusField.meta?.sort || 0)
    );
}

// Generate Zod schema from form fields
export function generateZodSchema(fields) {
  const schemaFields = {};

  fields.forEach((field) => {
    let zodField;

    switch (field.type) {
      case "text":
      case "textarea":
      case "password":
        zodField = requiredString();
        break;

      case "number":
        zodField = z.number();
        break;

      case "date":
      case "time":
      case "datetime-local":
        zodField = requiredString();
        break;

      case "checkbox":
        zodField = z.boolean().default(false);
        break;

      case "email":
        zodField = requiredString().email();
        break;

      case "url":
        zodField = requiredString().url();
        break;

      case "select":
      case "multiselect":
        zodField = requiredString();
        break;

      case "json":
        zodField = z.any();
        break;

      case "file":
        zodField = z.any();
        break;

      default:
        zodField = z.any();
    }

    // Apply required validation
    if (!field.required) {
      zodField = zodField.optional().nullable();
    }

    // Apply custom validation rules
    if (field.validation) {
      if (field.validation.min) {
        zodField = zodField.min(field.validation.min, {
          message: field.validationMessage,
        });
      }
      if (field.validation.max) {
        zodField = zodField.max(field.validation.max, {
          message: field.validationMessage,
        });
      }
    }

    schemaFields[field.name] = zodField;
  });

  return z.object(schemaFields);
}

// Get field component type based on field configuration
export function getFieldComponent(field) {
  // Handle special interfaces
  if (field.directusField?.meta?.interface) {
    const interface_ = field.directusField.meta.interface;

    switch (interface_) {
      case "select-dropdown":
      case "select-dropdown-m2o":
        return "select";
      case "select-radio":
        return "radio";
      case "select-multiple-dropdown":
        return "multiselect";
      case "input-rich-text-html":
      case "input-rich-text-md":
        return "richtext";
      case "file-image":
      case "files":
        return "file";
      case "dateTime":
      case "date":
      case "datetime-local":
        return "datetime";
      case "boolean":
        return "switch";
      case "slider":
        return "slider";
      case "tags":
        return "tags";
      default:
        return field.type;
    }
  }

  return field.type;
}
